import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, ArrowLeft, TrendingDown, Sparkles, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

function Inner() {
  const { activeId } = useCompany();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [losses, setLosses] = useState([]);
  const [chainResult, setChainResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [adding, setAdding] = useState({ ay_incurred: "2024-25", head: "business", amount: "", remarks: "" });

  const load = async () => {
    try {
      const [r, l] = await Promise.all([
        api.get(`/tax/reference`),
        api.get(`/companies/${activeId}/tax/losses`),
      ]);
      setReference(r.data);
      setLosses(l.data);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  useEffect(() => {
    if (activeId) load();
  }, [activeId]);

  const runChain = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/losses/auto-chain`);
      setChainResult(data);
      const applied = data.years.filter((y) => y.setoff_applied > 0 || (y.carried_forward || []).length);
      toast.success(applied.length
        ? `Set off ₹${formatINR(data.total_setoff_applied)} across ${data.years.length} year(s)`
        : "Nothing to set off yet — add prior-year losses or year computations");
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setRunning(false);
    }
  };

  const addLoss = async () => {
    if (!adding.amount || Number(adding.amount) <= 0) {
      toast.error("Amount must be > 0");
      return;
    }
    try {
      await api.post(`/companies/${activeId}/tax/losses`, {
        ...adding,
        amount: Number(adding.amount),
      });
      toast.success("Loss added");
      setAdding({ ay_incurred: "2024-25", head: "business", amount: "", remarks: "" });
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const deleteLoss = async (id) => {
    if (!window.confirm("Delete this loss entry?")) return;
    try {
      await api.delete(`/companies/${activeId}/tax/losses/${id}`);
      toast.success("Loss deleted");
      await load();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const totalAvailable = losses.reduce((s, l) => s + Number(l.available || 0), 0);

  if (!reference) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <>
      <PageHeader
        title="Loss Carry-Forward Ledger"
        subtitle="Losses eligible for set-off against future income of the same head (Sections 71/72)."
        actions={
          <>
            <Button onClick={runChain} disabled={running} className="h-8 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-auto-chain">
              <Sparkles className="w-4 h-4 mr-1" /> {running ? "Calculating…" : "Auto carry-forward & set-off"}
            </Button>
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-losses-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </>
        }
      />
      <div className="p-6 max-w-5xl space-y-4">
        {/* Auto chain results */}
        {chainResult && (
          <div className="border border-emerald-200 bg-emerald-50/50 rounded p-4" data-testid="chain-result">
            <div className="flex items-center gap-2 mb-2">
              <ArrowRightLeft className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-semibold text-emerald-900">Auto set-off applied — ₹{formatINR(chainResult.total_setoff_applied)} total</span>
            </div>
            <div className="overflow-x-auto">
              <table className="tx-table w-full text-xs" data-testid="chain-result-table">
                <thead><tr><th>Assessment Year</th><th className="text-right">B/F loss set off</th><th>From year(s)</th><th>New loss carried forward</th></tr></thead>
                <tbody>
                  {chainResult.years.map((y) => (
                    <tr key={y.ay}>
                      <td className="font-mono">{y.ay}</td>
                      <td className="text-right font-mono font-semibold text-emerald-700">{y.setoff_applied > 0 ? `₹ ${formatINR(y.setoff_applied)}` : "—"}</td>
                      <td className="text-gray-600">{(y.setoff_breakdown || []).map((b) => `${b.head} AY ${b.from_ay} ₹${formatINR(b.amount)}`).join("; ") || "—"}</td>
                      <td className="text-gray-600">{(y.carried_forward || []).map((c) => `${c.head} ₹${formatINR(c.amount)}`).join("; ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-emerald-800 mt-2">Business & House-Property losses are auto set off against future same-head income (Sec 72 / 71B) and reflected in each year's computation. Capital & speculation losses are carried forward here — apply them in the Capital-Gains working.</p>
          </div>
        )}

        {/* Summary card */}
        <div className="border border-blue-200 bg-blue-50/40 rounded p-4 flex items-center gap-3">
          <TrendingDown className="w-8 h-8 text-blue-700" />
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-900 font-semibold">Total available for set-off</div>
            <div className="text-2xl font-bold font-mono text-blue-900" data-testid="losses-total-available">₹ {formatINR(totalAvailable)}</div>
            <div className="text-[11px] text-blue-800 mt-0.5">Across {losses.length} loss entries</div>
          </div>
        </div>

        {/* Add loss form */}
        <div className="border border-gray-200 bg-white rounded p-4 space-y-3" data-testid="add-loss-form">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">Add a Carry-Forward Loss</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1">
              <label className="text-[11px] uppercase text-gray-600">AY Incurred</label>
              <Input value={adding.ay_incurred} onChange={(e) => setAdding({ ...adding, ay_incurred: e.target.value })} placeholder="2024-25" className="h-9 font-mono" data-testid="input-loss-ay" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase text-gray-600">Head of Loss</label>
              <select
                value={adding.head}
                onChange={(e) => setAdding({ ...adding, head: e.target.value })}
                className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                data-testid="select-loss-head"
              >
                {(reference.loss_heads || []).map((h) => (
                  <option key={h.code} value={h.code}>{h.label} ({h.expiry_years}y)</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase text-gray-600">Amount</label>
              <Input type="number" value={adding.amount} onChange={(e) => setAdding({ ...adding, amount: e.target.value })} className="h-9 text-right font-mono" data-testid="input-loss-amount" />
            </div>
            <div className="space-y-1 md:col-span-1">
              <label className="text-[11px] uppercase text-gray-600">Remarks</label>
              <Input value={adding.remarks} onChange={(e) => setAdding({ ...adding, remarks: e.target.value })} className="h-9 text-xs" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase text-gray-600">&nbsp;</label>
              <Button onClick={addLoss} className="h-9 bg-blue-700 hover:bg-blue-800 w-full" data-testid="btn-add-loss">
                <Plus className="w-4 h-4 mr-1" /> Add Loss
              </Button>
            </div>
          </div>
        </div>

        {/* Losses table */}
        {losses.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded p-10 text-center bg-white">
            <TrendingDown className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No losses recorded yet.</p>
            <p className="text-xs text-gray-500 mt-1">Add prior-year losses above so they can be set off in future computations.</p>
          </div>
        ) : (
          <div className="border border-gray-200 bg-white rounded overflow-x-auto">
            <table className="tx-table w-full" data-testid="losses-table">
              <thead>
                <tr>
                  <th>AY Incurred</th>
                  <th>Head</th>
                  <th>Source</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Utilized</th>
                  <th className="text-right">Available</th>
                  <th>Expiry AY</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {losses.map((l) => {
                  const util = (l.utilized || []).reduce((s, u) => s + Number(u.amount || 0), 0);
                  const label = (reference.loss_heads || []).find((h) => h.code === l.head)?.label || l.head;
                  const isAuto = l.source === "auto";
                  return (
                    <tr key={l.id} data-testid={`loss-row-${l.id}`}>
                      <td className="font-mono text-xs">{l.ay_incurred}</td>
                      <td className="text-xs">{label}</td>
                      <td>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isAuto ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`} data-testid={`loss-source-${l.id}`}>
                          {isAuto ? "Auto" : "Manual"}
                        </span>
                      </td>
                      <td className="text-right font-mono text-xs">₹ {formatINR(l.amount)}</td>
                      <td className="text-right font-mono text-xs text-gray-500">
                        ₹ {formatINR(util)}
                        {util > 0 && (
                          <div className="text-[10px] text-gray-400 font-normal">
                            {(l.utilized || []).map((u) => `AY ${u.ay_used}: ₹${formatINR(u.amount)}`).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="text-right font-mono text-xs font-semibold text-emerald-700">₹ {formatINR(l.available)}</td>
                      <td className="font-mono text-xs">{l.expiry_ay}</td>
                      <td>
                        {isAuto ? (
                          <span className="text-[10px] text-gray-400" title="Auto entries are derived from your year computations and regenerate automatically">auto</span>
                        ) : (
                          <button onClick={() => deleteLoss(l.id)} className="text-red-600 hover:text-red-800" data-testid={`btn-delete-loss-${l.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3 leading-relaxed">
          <strong>Automatic carry-forward & set-off:</strong> Every time you save a year's computation, AXIORA
          re-derives this ledger — any net Business or House-Property loss is carried forward automatically
          (shown as <em>Auto</em>) and set off against the same head's income in later years (oldest loss first,
          within the statutory limit: Business & House Property 8 years, Speculation 4 years, Capital losses 8 years).
          The set-off is written back into each year's Tax Computation as brought-forward set-off. Use
          <strong> Auto carry-forward &amp; set-off</strong> above to re-run it on demand. Add <em>Manual</em> entries
          for losses from years you haven't computed here. Capital &amp; speculation losses are carried forward and
          tracked; apply their set-off in the Capital-Gains working.
        </div>
      </div>
    </>
  );
}

export default function TaxLosses() {
  return <RequireCompany><Inner /></RequireCompany>;
}
